import React, { useState, useEffect } from 'react';
import { 
  Plus, Settings, Eye, Edit, Trash2, Save, X, BarChart3, Users, MessageSquare, 
  Bell, ChevronDown, ChevronRight, AlertCircle, CheckCircle, Clock, Target, 
  Link2, Activity, TrendingUp, RefreshCw, Filter, Search, Image, Video,
  ThumbsUp, ThumbsDown, Upload, ExternalLink
} from 'lucide-react';
import {
  getAllPulsePosts,
  getModerationQueue,
  updatePulsePost,
  deletePulsePost,
  approvePulsePost,
  rejectPulsePost,
  getPulseAnalytics,
  approvePulsePostWithVimeo,
  transferVideoToVimeo
} from '../utils/pulseAdminApi';
import { isCloudinaryVideo } from '../utils/vimeoApi';
import CreatePulsePost from './CreatePulsePost';
import '../styles/PulseAdmin.css';

const PulseAdmin = ({ adminUser }) => {
  const [activeTab, setActiveTab] = useState('all-posts');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // View state
  const [showCreatePage, setShowCreatePage] = useState(false);
  
  // Pulse posts state
  const [posts, setPosts] = useState([]);
  const [totalPosts, setTotalPosts] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage] = useState(20);
  
  // Filters state
  const [filters, setFilters] = useState({
    status: '',
    post_type: '',
    user_id: '',
    search: '',
    date_from: '',
    date_to: ''
  });
  
  // Moderation queue
  const [moderationQueue, setModerationQueue] = useState([]);
  
  // Analytics
  const [analytics, setAnalytics] = useState({
    totalPosts: 0,
    pendingApproval: 0,
    recentPosts: 0,
    postTypeCount: {}
  });
  
  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);

  // Load data on mount
  useEffect(() => {
    loadInitialData();
  }, []);

  // Reload posts when filters or page changes
  useEffect(() => {
    loadPosts();
  }, [filters, currentPage]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [postsData, queueData, analyticsData] = await Promise.all([
        getAllPulsePosts(filters, postsPerPage, (currentPage - 1) * postsPerPage),
        getModerationQueue(),
        getPulseAnalytics()
      ]);
      
      setPosts(postsData.posts);
      setTotalPosts(postsData.total);
      setModerationQueue(queueData);
      setAnalytics(analyticsData);
    } catch (err) {
      console.error('Error loading pulse data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadPosts = async () => {
    try {
      const postsData = await getAllPulsePosts(filters, postsPerPage, (currentPage - 1) * postsPerPage);
      setPosts(postsData.posts);
      setTotalPosts(postsData.total);
    } catch (err) {
      console.error('Error loading posts:', err);
      setError(err.message);
    }
  };

  const handleUpdatePost = async () => {
    if (!selectedPost) return;

    try {
      const updated = await updatePulsePost(selectedPost.id, {
        text_content: selectedPost.text_content,
        status: selectedPost.status,
        post_type: selectedPost.post_type,
        approved: selectedPost.approved
      });
      
      setPosts(prev => prev.map(p => p.id === updated.id ? updated : p));
      setShowEditModal(false);
      setSelectedPost(null);
      alert('Post updated successfully!');
    } catch (err) {
      console.error('Error updating post:', err);
      alert('Failed to update post: ' + err.message);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!confirm('Are you sure you want to delete this post?')) return;

    try {
      await deletePulsePost(postId);
      setPosts(prev => prev.filter(p => p.id !== postId));
      alert('Post deleted successfully!');
    } catch (err) {
      console.error('Error deleting post:', err);
      alert('Failed to delete post: ' + err.message);
    }
  };

  const handleApprovePost = async (postId) => {
    try {
      // Use the Vimeo-enabled approval function
      // This will automatically transfer Cloudinary videos to Vimeo
      const result = await approvePulsePostWithVimeo(postId);
      
      setModerationQueue(prev => prev.filter(p => p.id !== postId));
      loadPosts(); // Reload to show updated post
      
      if (result.vimeo_transfer_error) {
        alert('Post approved! ⚠️ Note: Video transfer to Vimeo failed. You can retry manually.');
      } else if (result.vimeo_id) {
        alert('Post approved! ✅ Video transferred to Vimeo successfully!');
      } else {
        alert('Post approved!');
      }
    } catch (err) {
      console.error('Error approving post:', err);
      alert('Failed to approve post: ' + err.message);
    }
  };

  const handleTransferToVimeo = async (postId) => {
    if (!confirm('Transfer this video to Vimeo? This may take a minute.')) return;

    try {
      setLoading(true);
      const result = await transferVideoToVimeo(postId);
      loadPosts(); // Reload to show updated post
      alert(`Video transferred to Vimeo successfully! 🎉\nVimeo ID: ${result.vimeo_id}`);
    } catch (err) {
      console.error('Error transferring to Vimeo:', err);
      alert('Failed to transfer to Vimeo: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRejectPost = async (postId) => {
    if (!confirm('Reject this post? It will be set to Private.')) return;

    try {
      await rejectPulsePost(postId);
      setModerationQueue(prev => prev.filter(p => p.id !== postId));
      alert('Post rejected and set to Private');
    } catch (err) {
      console.error('Error rejecting post:', err);
      alert('Failed to reject post: ' + err.message);
    }
  };

  // Callback when post is created successfully
  const handlePostCreated = () => {
    setShowCreatePage(false);
    loadPosts(); // Refresh the posts list
    loadInitialData(); // Refresh analytics
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  /**
   * Dashboard Tab
   */
  const DashboardTab = () => (
    <div className="pulse-dashboard">
      <div className="dashboard-stats-grid">
        <div className="stat-card">
          <div className="stat-icon blue">
            <MessageSquare />
          </div>
          <div className="stat-content">
            <h3>{analytics.totalPosts.toLocaleString()}</h3>
            <p>Total Posts</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon orange">
            <Clock />
          </div>
          <div className="stat-content">
            <h3>{analytics.pendingApproval}</h3>
            <p>Pending Approval</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon green">
            <TrendingUp />
          </div>
          <div className="stat-content">
            <h3>{analytics.recentPosts}</h3>
            <p>Last 7 Days</p>
          </div>
        </div>
      </div>

      <div className="post-type-breakdown">
        <h3>Posts by Type</h3>
        <div className="type-grid">
          {Object.entries(analytics.postTypeCount).map(([type, count]) => (
            <div key={type} className="type-item">
              <span className="type-label">{type}</span>
              <span className="type-count">{count}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="quick-actions">
        <button className="action-btn primary" onClick={() => setShowCreateModal(true)}>
          <Plus size={20} />
          Create Post
        </button>
        <button className="action-btn" onClick={() => setActiveTab('moderation')}>
          <Bell size={20} />
          View Moderation Queue ({analytics.pendingApproval})
        </button>
        <button className="action-btn" onClick={loadInitialData}>
          <RefreshCw size={20} />
          Refresh Data
        </button>
      </div>
    </div>
  );

  /**
   * All Posts Tab
   */
  const AllPostsTab = () => (
    <div className="all-posts-tab">
      {/* Filters */}
      <div className="filters-bar">
        <div className="filter-group">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search text content..."
            value={filters.search}
            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
          />
        </div>

        <select
          value={filters.status}
          onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
        >
          <option value="">All Status</option>
          <option value="Public">Public</option>
          <option value="Private">Private</option>
          <option value="Draft">Draft</option>
        </select>

        <select
          value={filters.media_type || ''}
          onChange={(e) => setFilters(prev => ({ ...prev, media_type: e.target.value }))}
        >
          <option value="">All Media Types</option>
          <option value="image">📷 Image</option>
          <option value="video">🎥 Video</option>
          <option value="text">📝 Text Only</option>
          <option value="none">⭕ No Media</option>
        </select>

        <select
          value={filters.post_type}
          onChange={(e) => setFilters(prev => ({ ...prev, post_type: e.target.value }))}
        >
          <option value="">All Types</option>
          <option value="artwork">Artwork</option>
          <option value="collection">Collection</option>
          <option value="event">Event</option>
          <option value="gallery">Gallery</option>
          <option value="milestone">Milestone</option>
          <option value="inspiration">Inspiration</option>
          <option value="personal">Personal</option>
          <option value="other">Other</option>
        </select>

        <button className="btn-filter" onClick={() => setFilters({})}>
          Clear Filters
        </button>
      </div>

      {/* Posts List */}
      <div className="posts-list">
        {posts.map(post => (
          <div key={post.id} className="post-card">
            <div className="post-media">
              {post.media_type === 'video' ? (
                <video src={post.source} controls style={{ width: '100%', maxHeight: '300px' }} />
              ) : (
                <img src={post.source} alt="Post media" />
              )}
            </div>

            <div className="post-details">
              <div className="post-header">
                <div className="user-info">
                  <img src={post.users?.profile_img || '/default-avatar.png'} alt="User" className="user-avatar" />
                  <div>
                    <strong>{post.users?.nickname || post.users?.handle || 'Unknown User'}</strong>
                    <span className="post-date">{formatDate(post.created_at)}</span>
                  </div>
                </div>

                <div className="post-badges">
                  <span className={`badge status-${post.status?.toLowerCase()}`}>{post.status}</span>
                  <span className="badge type">{post.post_type}</span>
                  {post.media_type && (
                    <span className="badge media">
                      {post.media_type === 'video' ? <Video size={14} /> : <Image size={14} />}
                      {post.media_type}
                    </span>
                  )}
                </div>
              </div>

              <div className="post-content">
                <p>{post.text_content || <em>No caption</em>}</p>
              </div>

              <div className="post-meta">
                <span>ID: {post.id}</span>
                {post.vc_id && <span>Artwork: {post.vc_id}</span>}
                {post.media_source && <span>Source: {post.media_source}</span>}
              </div>

              <div className="post-actions">
                <button className="action-btn edit" onClick={() => { setSelectedPost(post); setShowEditModal(true); }}>
                  <Edit size={16} />
                  Edit
                </button>
                <button className="action-btn delete" onClick={() => handleDeletePost(post.id)}>
                  <Trash2 size={16} />
                  Delete
                </button>
                {post.needs_approval && !post.approved && (
                  <>
                    <button className="action-btn approve" onClick={() => handleApprovePost(post.id)}>
                      <ThumbsUp size={16} />
                      Approve
                    </button>
                    <button className="action-btn reject" onClick={() => handleRejectPost(post.id)}>
                      <ThumbsDown size={16} />
                      Reject
                    </button>
                  </>
                )}
                {post.media_type === 'video' && isCloudinaryVideo(post.source) && (
                  <button 
                    className="action-btn vimeo" 
                    onClick={() => handleTransferToVimeo(post.id)}
                    title="Transfer this video to Vimeo for better streaming"
                  >
                    <Video size={16} />
                    Transfer to Vimeo
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      <div className="pagination">
        <button
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(prev => prev - 1)}
        >
          Previous
        </button>
        <span>Page {currentPage} of {Math.ceil(totalPosts / postsPerPage)}</span>
        <button
          disabled={currentPage * postsPerPage >= totalPosts}
          onClick={() => setCurrentPage(prev => prev + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );

  /**
   * Moderation Tab
   */
  const ModerationTab = () => (
    <div className="moderation-tab">
      <h2>Moderation Queue ({moderationQueue.length} pending)</h2>
      
      {moderationQueue.length === 0 ? (
        <div className="empty-state">
          <CheckCircle size={48} />
          <p>No posts pending approval</p>
        </div>
      ) : (
        <div className="posts-list">
          {moderationQueue.map(post => (
            <div key={post.id} className="post-card moderation">
              <div className="post-media">
                {post.media_type === 'video' ? (
                  <video src={post.source} controls style={{ width: '100%', maxHeight: '300px' }} />
                ) : (
                  <img src={post.source} alt="Post media" />
                )}
              </div>

              <div className="post-details">
                <div className="user-info">
                  <img src={post.users?.profile_img || '/default-avatar.png'} alt="User" className="user-avatar" />
                  <div>
                    <strong>{post.users?.nickname || post.users?.handle}</strong>
                    <span>{post.users?.email}</span>
                  </div>
                </div>

                <p className="post-content">{post.text_content}</p>

                <div className="post-meta">
                  <span>Posted: {formatDate(post.created_at)}</span>
                  <span>Type: {post.post_type}</span>
                </div>

                <div className="moderation-actions">
                  <button className="btn success" onClick={() => handleApprovePost(post.id)}>
                    <ThumbsUp size={18} />
                    Approve {post.media_type === 'video' && isCloudinaryVideo(post.source) && '+ Transfer to Vimeo'}
                  </button>
                  <button className="btn danger" onClick={() => handleRejectPost(post.id)}>
                    <ThumbsDown size={18} />
                    Reject
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  /**
   * Edit Post Modal
   */
  const EditPostModal = () => {
    if (!selectedPost) return null;

    return (
      <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Edit Post #{selectedPost.id}</h2>
            <button className="close-btn" onClick={() => setShowEditModal(false)}>
              <X size={24} />
            </button>
          </div>

          <div className="modal-body">
            <div className="form-group">
              <label>Caption</label>
              <textarea
                rows={4}
                value={selectedPost.text_content || ''}
                onChange={(e) => setSelectedPost(prev => ({ ...prev, text_content: e.target.value }))}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Status</label>
                <select
                  value={selectedPost.status}
                  onChange={(e) => setSelectedPost(prev => ({ ...prev, status: e.target.value }))}
                >
                  <option value="Public">Public</option>
                  <option value="Private">Private</option>
                  <option value="Draft">Draft</option>
                </select>
              </div>

              <div className="form-group">
                <label>Post Type</label>
                <select
                  value={selectedPost.post_type}
                  onChange={(e) => setSelectedPost(prev => ({ ...prev, post_type: e.target.value }))}
                >
                  <option value="artwork">Artwork</option>
                  <option value="collection">Collection</option>
                  <option value="event">Event</option>
                  <option value="gallery">Gallery</option>
                  <option value="milestone">Milestone</option>
                  <option value="inspiration">Inspiration</option>
                  <option value="personal">Personal</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={selectedPost.approved}
                  onChange={(e) => setSelectedPost(prev => ({ ...prev, approved: e.target.checked }))}
                />
                Approved
              </label>
            </div>
          </div>

          <div className="modal-footer">
            <button className="btn secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </button>
            <button className="btn primary" onClick={handleUpdatePost}>
              <Save size={18} />
              Save Changes
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="loading-spinner">Loading pulse data...</div>;
  }

  // If showing create page, render that instead
  if (showCreatePage) {
    return (
      <CreatePulsePost 
        onBack={() => setShowCreatePage(false)}
        onSuccess={handlePostCreated}
      />
    );
  }

  return (
    <div className="pulse-admin">
      <div className="admin-header">
        <h1>Pulse Admin</h1>
        <button className="btn primary" onClick={() => setShowCreatePage(true)}>
          <Plus size={20} />
          Create Post
        </button>
      </div>

      {error && (
        <div className="error-banner">
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      <div className="tabs">
        <button
          className={activeTab === 'dashboard' ? 'active' : ''}
          onClick={() => setActiveTab('dashboard')}
        >
          <BarChart3 size={18} />
          Dashboard
        </button>
        <button
          className={activeTab === 'all-posts' ? 'active' : ''}
          onClick={() => setActiveTab('all-posts')}
        >
          <MessageSquare size={18} />
          All Posts
        </button>
        <button
          className={activeTab === 'moderation' ? 'active' : ''}
          onClick={() => setActiveTab('moderation')}
        >
          <Bell size={18} />
          Moderation
          {analytics.pendingApproval > 0 && (
            <span className="badge-count">{analytics.pendingApproval}</span>
          )}
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'dashboard' && <DashboardTab />}
        {activeTab === 'all-posts' && <AllPostsTab />}
        {activeTab === 'moderation' && <ModerationTab />}
      </div>

      {showEditModal && <EditPostModal />}
    </div>
  );
};

export default PulseAdmin;
